import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { SelectItem } from '@/components/FormElements/Select/Select';
import { useKaseContext } from '@/hooks';
import { IFile, IUser, IOffenseOption, IProperty } from '@/models';
import { IVendor } from '@/models/vendors';

import {
  getDropdownItems,
  getItemDynamicOptions,
} from '../../../../KaseCrimes/utilities';
import { useKaseArrestsContext } from '../../../KaseArrestsContext';
import { FormFieldRenderer, NewCourtVendorModal } from '../components';
import { arrestInfoFieldGroups } from '../data';
import { IArrestFormData } from '../types';

interface ArrestInformationTabProps {
  viewMode?: boolean;
  users: IUser[];
  policeUsers: IUser[];
  filteredOffenseOptions: IOffenseOption[];
  agencies: SelectItem[];
  courts: SelectItem[];
  closedCfsOptions: SelectItem[];
  otherArrests: { arrestId: string; masterNameId: string }[];
  caseProperties: IProperty[];
  refetchCourts: () => Promise<SelectItem[]>;
}

const ArrestInformationTab = ({
  viewMode = false,
  users = [],
  policeUsers = [],
  filteredOffenseOptions = [],
  agencies = [],
  courts = [],
  closedCfsOptions = [],
  otherArrests = [],
  caseProperties = [],
  refetchCourts,
}: ArrestInformationTabProps) => {
  const { currentArrestee, setCurrentArrestee } = useKaseArrestsContext();
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<IArrestFormData>();
  const { kase } = useKaseContext();

  // Modal state for adding new court vendor
  const [isCourtModalOpen, setIsCourtModalOpen] = useState(false);
  const [activeCourtField, setActiveCourtField] = useState<
    'arraignmentCourt' | 'returnCourt' | null
  >(null);

  const arrestDate = watch('arrestDate');
  const arrestTime = watch('arrestTime');
  const miranda = watch('miranda');
  // Sync arrestTime when arrestDate changes (extract time from date)
  useEffect(() => {
    if (arrestDate) {
      const date =
        arrestDate instanceof Date ? arrestDate : new Date(arrestDate);
      if (!isNaN(date.getTime())) {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const timeString = `${hours}:${minutes}`;

        // Only update if different to avoid infinite loop
        if (arrestTime !== timeString) {
          setValue('arrestTime', timeString);
        }
      }
    }
  }, [arrestDate]);

  // Sync arrestDate when arrestTime changes (merge time with date)
  useEffect(() => {
    if (arrestTime && arrestDate) {
      const date =
        arrestDate instanceof Date ? arrestDate : new Date(arrestDate);
      if (!isNaN(date.getTime())) {
        const [hours, minutes] = arrestTime.split(':').map(Number);
        const newDate = new Date(date);
        newDate.setHours(hours);
        newDate.setMinutes(minutes);

        // Only update if different to avoid infinite loop
        if (date.getTime() !== newDate.getTime()) {
          setValue('arrestDate', newDate);
        }
      }
    }
  }, [arrestTime]);

  // Clear Miranda-related fields when Miranda is set to 'NO'
  useEffect(() => {
    if (miranda === 'NO') {
      setValue('mirandaBy', undefined);
      setValue('mirandaDate', null);
      setValue('mirandaTime', '');
    }
  }, [miranda, setValue]);

  // Auto-populate incidentNumber with cfsParent if not already set
  // search through closedCfsOptions and find the CFS with the matching ID
  useEffect(() => {
    const currentIncidentNumber = watch('incidentNumber');
    if (kase?.cfsParent && !currentIncidentNumber) {
      const foundCfs = closedCfsOptions.find(
        (cfs) => cfs.value?.toString() === kase.cfsParent?.toString(),
      );
      if (foundCfs) {
        setValue('incidentNumber', foundCfs.value as string);
      }
    }
  }, [kase?.cfsParent, closedCfsOptions]);

  const checkArresteePropertyAndEvidence = (properties: any[]) => {
    if (!currentArrestee?.masterName?._id?.toString() || !properties.length) {
      return;
    }

    if (watch('property') && watch('evidence') && watch('processedBy')) {
      return;
    }

    const arresteeMasterNameId =
      typeof currentArrestee.masterName === 'string'
        ? currentArrestee.masterName
        : currentArrestee.masterName._id?.toString();

    const arresteeProperty: IProperty | undefined = properties.find(
      (property) => {
        const ownerId =
          typeof property.owner === 'string'
            ? property.owner
            : property.owner?._id?.toString();
        return ownerId === arresteeMasterNameId;
      },
    );

    // Check if arrestee owns any property
    const ownsProperty = arresteeProperty !== undefined;

    // Check if arrestee owns any evidence (property with isEvidence = true)
    const ownsEvidence = ownsProperty && arresteeProperty?.isEvidence === true;

    const loggedByUserId =
      arresteeProperty?.loggedInfo?.loggedBy?._id?.toString() || undefined;

    // Auto-populate property field if not already set
    if (!watch('property')) {
      setValue('property', ownsProperty ? 'YES' : 'NO');
    }

    // Auto-populate evidence field if not already set
    if (!watch('evidence')) {
      setValue('evidence', ownsEvidence ? 'YES' : 'NO');
    }

    // Auto-populate processedBy with loggedBy user Id from properties if not already set
    if (!watch('processedBy')) {
      setValue('processedBy', loggedByUserId);
    }
  };

  useEffect(() => {
    checkArresteePropertyAndEvidence(caseProperties);
  }, [caseProperties, setValue]);

  const handleUseIncidentAddress = () => {
    if (kase?.address) {
      setValue('arrestLocation', kase.address);
    }
  };

  // Handlers for adding new court vendor
  const handleAddNewCourt = (fieldName: 'arraignmentCourt' | 'returnCourt') => {
    setActiveCourtField(fieldName);
    setIsCourtModalOpen(true);
  };

  const handleCourtCreated = async (newVendor: IVendor) => {
    // Refetch courts to update the dropdown
    await refetchCourts();

    // Auto-select the newly created court in the active field
    if (activeCourtField) {
      setValue(activeCourtField, String(newVendor._id));
    }

    // Reset modal state
    setIsCourtModalOpen(false);
    setActiveCourtField(null);
  };

  // Get dynamic items for form fields
  const getItems = (itemsKey: string): SelectItem[] => {
    const formValues = watch();

    switch (itemsKey) {
      case 'policeUsers':
        return policeUsers
          .filter((user): user is IUser & { _id: string } => !!user._id)
          .map((user) => ({
            label: user.fullName || user.email || 'Unknown',
            value: user._id,
          }));
      case 'users':
        return users
          .filter((user): user is IUser & { _id: string } => !!user._id)
          .map((user) => ({
            label: user.fullName || user.email || 'Unknown',
            value: user._id,
          }));
      case 'arrestLarcenyType':
        return getDropdownItems('larcenyType', filteredOffenseOptions) || [];
      case 'yesNo':
        return [
          { label: 'Yes', value: 'YES' },
          { label: 'No', value: 'NO' },
        ];
      case 'yesNoUnknown':
        return [
          { label: 'Yes', value: 'YES' },
          { label: 'No', value: 'NO' },
          { label: 'Unknown', value: 'UNKNOWN' },
        ];
      case 'NoFurtherYesNo':
        return [
          { label: 'No Further Process', value: 'NO_FURTHER_PROCESS' },
          { label: 'Yes', value: 'YES' },
          { label: 'No', value: 'NO' },
        ];
      case 'condition':
        return getDropdownItems('condition', filteredOffenseOptions) || [];
      case 'statementsList':
        return getDropdownItems('statements', filteredOffenseOptions) || [];
      case 'courtStatus':
        return getDropdownItems('courtStatus', filteredOffenseOptions) || [];
      case 'arrestStatus':
        return getDropdownItems('arrestStatus', filteredOffenseOptions) || [];
      case 'idProcedure':
        return getDropdownItems('idProcedure', filteredOffenseOptions) || [];
      case 'coDefendantsArrestNumber': {
        // Get current arrestee's masterName ID to exclude from the list
        const currentMasterNameId =
          typeof currentArrestee?.masterName === 'string'
            ? currentArrestee.masterName
            : currentArrestee?.masterName?._id?.toString();

        // Filter out current arrestee and map to SelectItem format
        return otherArrests
          .filter((arrest) => arrest.masterNameId !== currentMasterNameId)
          .map((arrest) => ({
            label: arrest.arrestId,
            value: arrest.arrestId,
          }));
      }
      case 'courtsList':
        return courts.length > 0
          ? courts
          : [{ label: 'Loading...', value: 'loading' }];
      case 'judges':
        return getDropdownItems('judges', filteredOffenseOptions) || [];
      case 'agencies':
        return agencies.length > 0
          ? agencies
          : [{ label: 'Loading...', value: 'loading' }];
      case 'closedCfs':
        return closedCfsOptions.length > 0
          ? closedCfsOptions
          : [{ label: 'Loading...', value: 'loading' }];
      default: {
        // Try to get dynamic options from offense configuration
        const currentItemOptions = getItemDynamicOptions<IArrestFormData>(
          itemsKey,
          filteredOffenseOptions,
          formValues,
        );
        if (currentItemOptions) {
          return currentItemOptions.items as SelectItem[];
        }
        return [];
      }
    }
  };

  // Hide "Use incident address" button if case has no address
  const hideIncidentButton = !kase?.address;

  return (
    <>
      <div className="space-y-4 w-full">
        {arrestInfoFieldGroups.map((group, groupIndex) => (
          <div key={`group-${groupIndex}`}>
            <div className="grid grid-cols-12 gap-4">
              {group.fields.map((field) => {
                // Disable Miranda-related fields when Miranda is 'NO'
                const isMirandaDisabled =
                  miranda === 'NO' &&
                  ['mirandaBy', 'mirandaDate', 'mirandaTime'].includes(
                    field.name,
                  );

                return (
                  <FormFieldRenderer
                    key={field.name}
                    field={{
                      ...field,
                      disabled: isMirandaDisabled || field.disabled,
                    }}
                    control={control}
                    errors={errors}
                    viewMode={viewMode}
                    getItems={getItems}
                    onUseIncidentAddress={
                      field.name === 'useIncidentButton'
                        ? handleUseIncidentAddress
                        : undefined
                    }
                    hideInput={
                      field.name === 'useIncidentButton'
                        ? hideIncidentButton
                        : undefined
                    }
                    onCustomChange={
                      field.name === 'arrestFiles'
                        ? (files: IFile[]) => {
                            if (!currentArrestee) return;
                            // Update both currentArrestee and form state
                            setCurrentArrestee({
                              ...currentArrestee,
                              files: files || [],
                            });
                            setValue('arrestFiles', files || []);
                          }
                        : undefined
                    }
                    onAddNewCourt={
                      field.name === 'arraignmentCourt' ||
                      field.name === 'returnCourt'
                        ? () =>
                            handleAddNewCourt(
                              field.name as 'arraignmentCourt' | 'returnCourt',
                            )
                        : undefined
                    }
                  />
                );
              })}
            </div>
            {groupIndex < arrestInfoFieldGroups.length - 1 && (
              <hr className="my-6 border-gray-300 dark:border-gray-600" />
            )}
          </div>
        ))}
      </div>

      {/* New Court Vendor Modal */}
      <NewCourtVendorModal
        isOpen={isCourtModalOpen}
        onClose={() => {
          setIsCourtModalOpen(false);
          setActiveCourtField(null);
        }}
        onSuccess={handleCourtCreated}
      />
    </>
  );
};

export default ArrestInformationTab;
